import Flood_MBTN from 'assets/FloodStageIcons/flood_stage_system.png';
import OVERWATCH_VMS_MBTN from 'assets/icons/exacq.png';
import MANUAL_ROAD_CLOSURE_MBTN from 'assets/map buttons/RescAlert Devices Manual Road Closures - Map Button.png';
import TECHNOLOGY_BRIDGE_SIREN_MBTN from 'assets/map buttons/RescAlert Devices Technology Bridge (Sirens-Giant Voice) - Map button.png';
import TECHNOLOGY_BRIDGE_MBTN from 'assets/map buttons/RescAlert Devices Technology Bridge - Map Button.png';
import FALLBACK from 'assets/resc_placeholder.png';
import CustomLeafletControl from 'components/BaseMap/components/CustomLeafletControl';
import { usePerms } from 'contexts/PermsContext';
import {
  selectLinkingGroupId,
  selectSelectedDeviceId,
  selectSelectedDeviceIndex,
} from 'features/integrations/state/selectors/deviceSelectors';
import { selectVisibleDeviceTypes } from 'features/integrations/state/selectors/integrationSelectors';
import {
  setLinkingGroupId,
  setSelectedDeviceId,
} from 'features/integrations/state/slices/deviceSlice';
import { setVisibleDeviceTypes } from 'features/integrations/state/slices/integrationSlice';
import { setSelectedDevice } from 'features/integrations/state/thunks';
import {
  DeviceIsDeviceType,
  DeviceIsSystemType,
} from 'features/integrations/utils/deviceComparisionUtils';
import { canViewDeviceType } from 'features/integrations/utils/integrationVisibilityUtils';
import { useState } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { FaList } from 'react-icons/fa';
import { GiBigWave } from 'react-icons/gi';
import { useDispatch, useSelector } from 'react-redux';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import '../assets/deviceMap.css';

const DeviceFilters = ({
  deviceTypeToFilter,
  selectedGroup,
  setSelectedGroup,
  setUseThisDevice,
  setMapBounds,
  setShowCurrentDevice,
  forceCompact = false,
}) => {
  // Tracks expanded/collapsed state for the mobile filter panel.
  const [mobileOpen, setMobileOpen] = useState(false);
  const perms = usePerms();
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const selectedDeviceIndex = useSelector(selectSelectedDeviceIndex);
  const selectedDeviceId = useSelector(selectSelectedDeviceId);
  const linkingGroupId = useSelector(selectLinkingGroupId);
  const {
    data: deviceGroups = [],
    isLoading: deviceGroupsLoading,
    isError: deviceGroupsError,
  } = useGetGroupsQuery();
  const dispatch = useDispatch();
  const visibleDeviceTypes = useSelector(selectVisibleDeviceTypes);

  // Map the incoming filter key to the device_type label stored on devices.
  const deviceTypeToFilterToType = (deviceType) => {
    switch (deviceType) {
      case 'stage':
        return 'Stage';
      case 'manualRoadClosure':
        return 'Manual Road Closure';
      case 'technologyBridge':
        return 'Technology Bridge_Flashing Lights';
      case 'technologyBridgeSiren':
        return 'Technology Bridge_Siren/Giant Voice';
      case 'overwatchVMS':
        return 'Overwatch VMS';
      case 'Monitoring Station':
        return 'Monitoring Station';
      default:
        return null;
    }
  };

  // Handles device/group selection and updates map bounds or current device.
  const handleDropdownChange = (e) => {
    if (e.target.value.startsWith('group:')) {
      const groupName = e.target.value.replace('group:', '');
      const group = deviceGroups.find((g) => g.name === groupName);
      setSelectedGroup(group); // Track selected group
      dispatch(setLinkingGroupId(group.id)); // Clears selectedDeviceId via reducer
      // Gather all device IDs from the group
      const allIds = [
        ...(group.devices || []),
        ...(group.controllers || []),
        ...(group.auxDevices || []),
      ];
      // Find all matching devices in nodeList
      const groupDevices = connectedDevices.filter((device) => allIds.includes(device.id));
      // Calculate bounds
      if (groupDevices.length > 0) {
        const lats = groupDevices.map((d) => parseFloat(d.location.lat));
        const lngs = groupDevices.map((d) => parseFloat(d.location.lng));
        const bounds = [
          [Math.min(...lats), Math.min(...lngs)],
          [Math.max(...lats), Math.max(...lngs)],
        ];
        setMapBounds(bounds); // This will zoom the map
      }
      return;
    }
    setSelectedGroup(null); // Clear group selection if a device is selected
    const selectedDevice = connectedDevices[e.target.value];
    dispatch(setSelectedDevice({ index: e.target.value }));
    dispatch(setSelectedDeviceId(selectedDevice?.id ?? null));
    setUseThisDevice(selectedDevice);
    const isDatasheetDevice =
      DeviceIsSystemType(selectedDevice, 'Flood') || selectedDevice?.system_type === 'USGS';
    if (isDatasheetDevice) {
      setShowCurrentDevice(true);
    } else {
      setShowCurrentDevice(false);
    }
  };

  const getFilterButtonClass = (key, compact) => {
    const base = visibleDeviceTypes[key] ? 'btn-primary' : 'btn-disabled';
    return `p-0 d-flex justify-content-center align-items-center ${base}${compact ? ' btn-sm' : ''}`;
  };

  // Shared renderer for the device/group selector; compact toggles mobile sizing.
  const renderDeviceSelect = (compact) => {
    const filteredDevices = connectedDevices
      .map((device, index) => ({ device, originalIndex: index }))
      .filter(({ device }) => {
        if (!deviceTypeToFilter) return true; // If no filter, include all devices
        return (
          device.device_type === deviceTypeToFilterToType(deviceTypeToFilter) &&
          (deviceTypeToFilter !== 'stage' || DeviceIsDeviceType(device, 'Stage')) &&
          canViewDeviceType(device, perms, visibleDeviceTypes)
        );
      });

    if (filteredDevices.length === 0) return null;

    // Sort by node_id
    const sortedDevices = filteredDevices.sort((a, b) => a.device.node_id - b.device.node_id);

    const groupedBySystem = new Map();

    for (const item of sortedDevices) {
      const system = item.device.system_type || 'Other';

      if (!groupedBySystem.has(system)) {
        groupedBySystem.set(system, []);
      }

      groupedBySystem.get(system).push(item);
    }

    // Decide what label to show in the dropdown.
    let selectedLabel;
    if (linkingGroupId != null && selectedGroup) {
      // if Group redux state valid, show group name
      selectedLabel = selectedGroup.name;
    } else {
      // if selectedDevice redux state valid, show device alias
      const byId =
        selectedDeviceId != null ? connectedDevices.find((d) => d.id === selectedDeviceId) : null;
      const found = byId ?? connectedDevices[selectedDeviceIndex];
      selectedLabel = found
        ? (found.device_type !== 'Manual Road Closure' ? found.static_id + ' ' : '') + found.alias
        : 'Select Device';
    }

    return (
      <Dropdown className="">
        <Dropdown.Toggle
          as={Button}
          variant="primary"
          className={'w-100 d-flex align-items-center' + (compact ? ' btn-sm' : '')}
        >
          <span className="text-truncate" style={{ maxWidth: 200 }}>
            {selectedLabel}
          </span>
        </Dropdown.Toggle>

        <Dropdown.Menu
          style={{ maxHeight: 300 }}
          className="w-100 overflow-y-auto overflow-x-hidden p-0"
        >
          {deviceGroups && deviceGroups.length > 0 && (
            <>
              <div
                className="dropdown-header text-center text-secondary"
                style={{ fontWeight: 600 }}
              >
                Device Groups
              </div>
              {deviceGroups.map((group) => (
                <Dropdown.Item
                  className="bg-primary-highlight text-white"
                  key={`group-${group.name}`}
                  onClick={() => handleDropdownChange({ target: { value: `group:${group.name}` } })}
                >
                  <div
                    className="text-truncate overflow-hidden text-overflow-ellipsis text-nowrap"
                    style={{ maxWidth: 260 }}
                  >
                    {group.name}
                  </div>
                </Dropdown.Item>
              ))}
            </>
          )}

          {/* Devices grouped by system type */}
          {[...groupedBySystem.entries()].map(([systemType, devices]) => (
            <div key={systemType}>
              <div
                className="dropdown-header text-center text-secondary"
                style={{ fontWeight: 600 }}
              >
                {systemType}
              </div>
              {devices.map(({ device, originalIndex }) => (
                <Dropdown.Item
                  className="bg-primary-highlight text-white"
                  key={`device-${device.node_id}-${originalIndex}`}
                  active={String(selectedDeviceIndex) === String(originalIndex)}
                  onClick={() => handleDropdownChange({ target: { value: `${originalIndex}` } })}
                >
                  <div
                    className="text-truncate overflow-hidden text-overflow-ellipsis text-nowrap fs-6"
                    style={{ maxWidth: 260 }}
                  >
                    {(device.device_type !== 'Manual Road Closure' &&
                    device.device_type !== 'Monitoring Station'
                      ? device.static_id + ' '
                      : '') + device.alias}
                  </div>
                </Dropdown.Item>
              ))}
            </div>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  // Shared renderer for the device-type filter buttons; compact toggles icon size.
  const renderFilterButtons = (compact) => {
    return (
      <>
        {perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewFloodStageConnections']) && (
          <Button
            className={getFilterButtonClass('Stage', compact)}
            onClick={() => {
              dispatch(
                setVisibleDeviceTypes({
                  ...visibleDeviceTypes,
                  Stage: !visibleDeviceTypes['Stage'],
                }),
              );
            }}
            style={{ width: 50, height: 50 }}
          >
            <span className="icon-frame" style={{ backgroundImage: `url(${Flood_MBTN})` }} />
          </Button>
        )}
        {perms.testPermission(
          [8, 9, 10, 11, 12, 13],
          ['ViewRescAlertDevicesManualRoadClosureConnections'],
        ) && (
          <Button
            className={getFilterButtonClass('Manual Road Closure', compact)}
            onClick={() => {
              dispatch(
                setVisibleDeviceTypes({
                  ...visibleDeviceTypes,
                  'Manual Road Closure': !visibleDeviceTypes['Manual Road Closure'],
                }),
              );
            }}
            style={{ width: 50, height: 50 }}
          >
            <span
              className="icon-frame"
              style={{ backgroundImage: `url(${MANUAL_ROAD_CLOSURE_MBTN})` }}
            />
          </Button>
        )}
        {perms.testPermission(
          [8, 9, 10, 11, 12, 13],
          ['ViewRescAlertDevicesTechnologyBridge_FlashingLightsConnections'],
        ) && (
          <Button
            className={getFilterButtonClass('Technology Bridge_Flashing Lights', compact)}
            onClick={() => {
              dispatch(
                setVisibleDeviceTypes({
                  ...visibleDeviceTypes,
                  'Technology Bridge_Flashing Lights':
                    !visibleDeviceTypes['Technology Bridge_Flashing Lights'],
                }),
              );
            }}
            style={{ width: 50, height: 50 }}
          >
            <span
              className="icon-frame"
              style={{ backgroundImage: `url(${TECHNOLOGY_BRIDGE_MBTN})` }}
            />
          </Button>
        )}
        {perms.testPermission(
          [8, 9, 10, 11, 12, 13],
          ['ViewRescAlertDevicesTechnologyBridge_Siren-GiantVoiceConnections'],
        ) && (
          <Button
            className={getFilterButtonClass('Technology Bridge_Siren/Giant Voice', compact)}
            onClick={() => {
              dispatch(
                setVisibleDeviceTypes({
                  ...visibleDeviceTypes,
                  'Technology Bridge_Siren/Giant Voice':
                    !visibleDeviceTypes['Technology Bridge_Siren/Giant Voice'],
                }),
              );
            }}
            style={{ width: 50, height: 50 }}
          >
            <span
              className="icon-frame"
              style={{ backgroundImage: `url(${TECHNOLOGY_BRIDGE_SIREN_MBTN})` }}
            />
          </Button>
        )}
        {perms.testPermission(
          [8, 9, 10, 11, 12, 13],
          ['ViewExacqVisionOverwatchVMSConnections'],
        ) && (
          <Button
            className={getFilterButtonClass('Overwatch VMS', compact)}
            onClick={() => {
              dispatch(
                setVisibleDeviceTypes({
                  ...visibleDeviceTypes,
                  'Overwatch VMS': !visibleDeviceTypes['Overwatch VMS'],
                }),
              );
            }}
            style={{ width: 50, height: 50 }}
          >
            <span
              className="icon-frame"
              style={{ backgroundImage: `url(${OVERWATCH_VMS_MBTN})` }}
            />
          </Button>
        )}
        {perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewUSGSMonitoringStationConnections']) && (
          <Button
            className={getFilterButtonClass('USGS', compact)}
            onClick={() => {
              dispatch(
                setVisibleDeviceTypes({
                  ...visibleDeviceTypes,
                  USGS: !visibleDeviceTypes['USGS'],
                }),
              );
            }}
            style={{ width: 50, height: 50 }}
          >
            <GiBigWave size={35} />
          </Button>
        )}
      </>
    );
  };

  // Force the compact filter UI for small widgets even on wide viewports.
  if (forceCompact) {
    return (
      <CustomLeafletControl position="topright">
        <div
          className="device-filters device-filters-mobile d-flex flex-column align-items-end gap-1"
          style={{ position: 'relative', zIndex: 900 }}
        >
          <button
            type="button"
            className="btn btn-secondary btn-md d-flex align-items-center gap-1"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-expanded={mobileOpen}
            aria-label="Toggle device filters"
          >
            <FaList size={14} />
            Filters
          </button>
          {mobileOpen && (
            <div className="device-filters-panel">
              <div className="mb-1">{renderDeviceSelect(true)}</div>
              <div className="d-flex flex-wrap justify-content-end gap-1">
                {renderFilterButtons(true)}
              </div>
            </div>
          )}
        </div>
      </CustomLeafletControl>
    );
  }

  return (
    <>
      {/* Controls: compact toggle that expands into a small overlay panel. */}
      <CustomLeafletControl position="topright">
        <div
          className="device-filters device-filters-mobile d-flex flex-column align-items-end gap-1"
          style={{ position: 'relative', zIndex: 900 }}
        >
          <button
            type="button"
            className="btn btn-secondary btn-md d-flex align-items-center gap-1"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-expanded={mobileOpen}
            aria-label="Toggle device filters"
          >
            <FaList size={14} />
            Filters
          </button>
          {mobileOpen && (
            <div className="device-filters-panel">
              <div className="mb-1">{renderDeviceSelect(true)}</div>
              <div className="d-flex flex-wrap justify-content-end gap-1">
                {renderFilterButtons(true)}
              </div>
            </div>
          )}
        </div>
      </CustomLeafletControl>
    </>
  );
};

export default DeviceFilters;
